import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useStore } from 'zustand';

import { parseScript, STATEMENTS, type ScriptError } from '@/domain/script';
import { EXAMPLE_SCRIPT, figurePrompt, grammarText, looksLikeAProblem } from '@/domain/script';
import { type EditorStore } from '@/store';

import { colour } from './theme';

/**
 * The problem editor, loaded when the panel is opened rather than at first paint.
 *
 * The same rule `MathField` follows for MathLive and for the same reason: ProseMirror is a real
 * framework, and putting it in the main bundle makes every user pay for it whether or not they
 * ever write a problem. A panel that has to be opened is exactly the boundary a chunk belongs
 * on.
 */
const ProblemEditor = lazy(() =>
  import('./editor/ProblemEditor').then((module) => ({ default: module.ProblemEditor }))
);

/**
 * Building a construction from written instructions.
 *
 * ## What it is for
 *
 * Three uses, and the third is the one that drove it. A teacher can type a figure faster
 * than they can click it; a worksheet can be kept as text under version control; and a
 * language model can be handed the grammar and asked for a diagram.
 *
 * That last one is why the *language* exists rather than the panel simply accepting the save
 * format. A generator writing JSON has to get entity ids, z-order and a schema version right
 * before it produces anything at all, and one wrong id yields a file that loads with half the
 * objects missing and no complaint. A line-based language reports *which line* was wrong, so
 * whoever wrote it — person or model — can fix that line.
 *
 * ## The grammar is copyable
 *
 * The button hands over the complete reference as text, which is exactly what a model needs
 * in its prompt. Making the user reconstruct it from this panel would be the difference
 * between a feature and a demo.
 */

/**
 * What the model proxy answers with. Declared here, not imported.
 *
 * `ui` may not reach `infrastructure` — the boundary lint says so and it is right: a panel that
 * knew how to call a model would be a panel that could not be rendered without one. It states
 * the shape it needs and `bootstrap` satisfies it, exactly as the file actions do.
 */
export type FigureOutcome =
  | { readonly script: string; readonly rounds?: number }
  | { readonly error: string; readonly cancelled?: boolean };

export interface ScriptPanelProps {
  readonly store: EditorStore;
  readonly onClose: () => void;
  /**
   * Turns a problem into a script.
   *
   * Optional, and its absence is a supported state rather than a broken one: without a proxy
   * the panel still writes and builds scripts, and still hands over the prompt to be asked
   * elsewhere. Only the one-button path disappears.
   *
   * Takes a signal because the wait is long enough to change your mind in — a hard problem
   * costs a second model call and has been measured at three minutes.
   */
  readonly onDrawFromProblem?: (problem: string, signal?: AbortSignal) => Promise<FigureOutcome>;
}

/**
 * How long to wait before saying that a second round may be running.
 *
 * Every figure that came back in one round was well inside this; the ones that took two were all
 * past it. So the message is not a guess dressed as fact — it is what the elapsed time makes
 * likely, worded as a possibility, and replaced by the truth as soon as the answer arrives.
 */
const SECOND_ROUND_AFTER = 20;

/**
 * What the empty box says.
 *
 * A real problem rather than an instruction, because the instruction is above it and what a
 * reader needs here is to recognise the shape of the thing being asked for. Taken from the
 * corpus, so it is a problem the feature is known to draw.
 */
const PLACEHOLDER =
  'Ví dụ: Cho hình chóp S.ABCD có đáy ABCD là hình vuông cạnh a, SA vuông góc với mặt phẳng ' +
  'đáy và SA = a. Gọi M là trung điểm của BC. Tính góc giữa SM và mặt phẳng (ABCD).';

export function ScriptPanel({ store, onClose, onDrawFromProblem }: ScriptPanelProps) {
  const commands = useStore(store, (state) => state.commands);

  /*
   * Two boxes, not one, and that is the change this panel needed most.
   *
   * `source` is the *problem* — prose a teacher pastes. `script` is the *figure* — what the
   * model wrote, or what an advanced user types directly. They used to share one field, which
   * meant the panel had to open showing one of them, and it opened showing the script: fifteen
   * lines of `A = point 0 0` in front of somebody who has never heard of a `point` and wants to
   * paste a problem. Everything else about the feature was aimed at people who are not
   * programmers, and the first screen asked them to be one.
   */
  const [source, setSource] = useState('');
  const [script, setScript] = useState('');
  const [errors, setErrors] = useState<readonly ScriptError[]>([]);
  const [built, setBuilt] = useState<number | null>(null);
  const [copied, setCopied] = useState<'grammar' | 'prompt' | null>(null);
  const [showScript, setShowScript] = useState(false);

  /*
   * Whether what was typed is a *problem* rather than a script.
   *
   * Pasting the problem statement is the first thing anybody tries — the panel is called "vẽ
   * bằng văn bản", and a problem is text. What came back was four lines of "unknown statement
   * `Cho`", which is true and useless: it describes the symptom and not the situation.
   */
  const isProblem = built !== null && looksLikeAProblem(built, errors.length);

  const [asking, setAsking] = useState(false);
  const [asked, setAsked] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [note, setNote] = useState<string | null>(null);
  const running = useRef<AbortController | null>(null);

  /*
   * A clock, while a request is out.
   *
   * The panel used to say "Đang vẽ…" and nothing else for as long as it took, which on a hard
   * problem is minutes — indistinguishable from a hung request, and the commonest thing a reader
   * does about that is press the button again. A number that moves is the difference between
   * waiting and wondering.
   */
  useEffect(() => {
    if (!asking) return;
    const started = Date.now();
    const tick = globalThis.setInterval(() => {
      setElapsed(Math.round((Date.now() - started) / 1000));
    }, 1000);
    return () => {
      globalThis.clearInterval(tick);
    };
  }, [asking]);

  // Nothing should still be in flight after the panel is gone.
  useEffect(
    () => () => {
      running.current?.abort();
    },
    []
  );

  /*
   * The whole feature, from a teacher's side: paste the problem, press one button.
   *
   * What comes back is a *script*, and it is put in the box rather than straight on the canvas.
   * A model can misread a problem, and the script is the place where that is visible and
   * fixable — dropping the figure onto the page and leaving the reader to work out which parts
   * were invented would be worse than showing them the sentence that produced it.
   */
  const draw = (): void => {
    if (onDrawFromProblem === undefined) return;
    const controller = new AbortController();
    running.current = controller;
    setAsking(true);
    setAsked(null);
    setNote(null);
    setElapsed(0);
    void onDrawFromProblem(source, controller.signal).then((result) => {
      running.current = null;
      setAsking(false);
      if ('error' in result) {
        // A cancellation is not an error to shout about: the reader asked for it.
        setAsked(result.cancelled === true ? null : result.error);
        setNote(result.cancelled === true ? 'Đã huỷ.' : null);
        return;
      }
      /*
       * Said afterwards rather than during, which is what one request and one response allows.
       * It still answers the question the wait raises — "was it stuck, or was it working?" —
       * and it names the round rather than the seconds, because the round is the reason.
       */
      setNote(
        result.rounds !== undefined && result.rounds > 1
          ? 'Mô hình phải sửa một lượt: bản đầu có dòng không dựng được.'
          : null
      );
      /*
       * The script lands in its own box and the problem stays in the reader's. That is what
       * lets them read one against the other — "the model put S over the centre, but my problem
       * says SA ⊥ đáy" — which was impossible when the answer overwrote the question.
       */
      setScript(result.script);
      setShowScript(true);
      const built = parseScript(result.script);
      setErrors(built.errors);
      setBuilt(built.created);
      if (built.created > 0) commands.insertDocument(built.document);
    });
  };

  const cancel = (): void => {
    running.current?.abort();
    running.current = null;
  };

  /*
   * Insert a piece of notation where the caret is, wrapped in `$…$`.
   *
   * Wrapped here rather than in the templates, so the delimiters are decided in one place and
   * a template stays the notation it is named after. A selection is replaced, which is what
   * every editor does and what makes "select the 3, press √" work.
   *
   * The caret lands inside the first empty pair — `\frac{|}{}` — because that is where the next
   * character goes. Without it, pressing the fraction button leaves the caret after the whole
   * thing and the teacher has to click back into it, which is the defect `grade.vn` recorded
   * having shipped and fixed.
   */
  const build = (): void => {
    const result = parseScript(script);
    setErrors(result.errors);
    setBuilt(result.created);
    // Whatever parsed is added, even when some lines failed — a partly correct script is
    // still worth most of its value, and the errors say what is missing.
    if (result.created > 0) commands.insertDocument(result.document);
  };

  const copy = (what: 'grammar' | 'prompt'): void => {
    const text = what === 'grammar' ? grammarText() : figurePrompt(source);
    void navigator.clipboard.writeText(text).then(
      () => {
        setCopied(what);
        globalThis.setTimeout(() => {
          setCopied(null);
        }, 2000);
      },
      () => {
        setCopied(null);
      }
    );
  };

  return (
    <div
      style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: 'min(720px, 92vw)',
        maxHeight: '86vh',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 'var(--es-radius-lg)',
        background: colour.solid,
        border: '1px solid var(--es-hairline-strong)',
        boxShadow: 'var(--es-shadow-2)',
        font: '13px/1.5 var(--es-font)',
        zIndex: 40,
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 14px',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
        }}
      >
        <strong style={{ font: '600 13px/1.3 var(--es-font)' }}>Vẽ hình từ đề bài</strong>
        <button
          type="button"
          onClick={onClose}
          aria-label="Đóng"
          style={{
            border: 'none',
            background: 'transparent',
            fontSize: 18,
            lineHeight: 1,
            cursor: 'pointer',
            color: colour.muted,
          }}
        >
          ×
        </button>
      </header>

      <div style={{ padding: 14, overflowY: 'auto' }}>
        {/*
          What this panel is for, in the words of the person using it.

          It used to open with fifteen lines of `A = point 0 0` in the box — the example script,
          as the initial state — and explain the language above it. So the first thing a teacher
          met was source code they had to select and delete before they could paste the problem
          they came with, and the panel read as a tool you have to learn before it helps you.
          The whole point of the feature is the opposite.
        */}
        <p style={{ margin: '0 0 8px', color: colour.body }}>
          Dán đề bài vào ô dưới rồi bấm <strong>Vẽ hình</strong>. Không cần biết cú pháp gì cả.
        </p>

        <div
          onKeyDown={(event) => {
            event.stopPropagation();
          }}
        >
          <Suspense
            fallback={
              <div
                className="es-field"
                style={{ minHeight: 160, padding: 10, color: colour.faint }}
              >
                Đang mở…
              </div>
            }
          >
            <ProblemEditor value={source} onChange={setSource} placeholder={PLACEHOLDER} />
          </Suspense>
        </div>

        {isProblem && (
          <div
            style={{
              margin: '10px 0 0',
              padding: '10px 12px',
              borderRadius: 'var(--es-radius-sm)',
              background: 'var(--es-accent-softer)',
              border: '1px solid var(--es-accent-ring)',
              color: 'var(--es-accent-ink)',
              font: '12px/1.6 var(--es-font)',
            }}
          >
            {onDrawFromProblem === undefined ? (
              <>
                <strong>Chưa có máy chủ mô hình.</strong> Bấm <em>Sao chép prompt</em> ở dưới, dán
                vào một trợ lý AI bất kỳ, rồi dán script nó trả về vào phần <em>Script</em>.
              </>
            ) : (
              <>
                <strong>Đây là đề bài.</strong> Bấm <em>Vẽ hình</em> để dựng hình minh hoạ.
              </>
            )}
          </div>
        )}

        {/*
          What is happening, while it happens.

          `aria-live` because this region is the only account of a wait that can run to minutes,
          and a reader who cannot see it has even less to go on than one who can.
        */}
        {asking && (
          <p
            aria-live="polite"
            style={{
              margin: '10px 0 0',
              padding: '8px 10px',
              borderRadius: 'var(--es-radius-sm)',
              background: 'var(--es-accent-softer)',
              border: '1px solid var(--es-accent-ring)',
              color: 'var(--es-accent-ink)',
              font: '12px/1.6 var(--es-font)',
            }}
          >
            Đang vẽ… {elapsed}s
            {elapsed >= SECOND_ROUND_AFTER && (
              <>
                {' — '}
                bản đầu có dòng chưa dựng được, mô hình đang sửa lại. Việc này mất thêm khoảng một
                phút.
              </>
            )}
          </p>
        )}

        {note !== null && !asking && (
          <p
            aria-live="polite"
            style={{
              margin: '10px 0 0',
              color: colour.muted,
              font: '12px/1.6 var(--es-font)',
            }}
          >
            {note}
          </p>
        )}

        {asked !== null && (
          <p
            role="alert"
            style={{
              margin: '10px 0 0',
              padding: '8px 10px',
              borderRadius: 'var(--es-radius-sm)',
              background: 'rgb(194 65 12 / 7%)',
              border: '1px solid rgb(194 65 12 / 18%)',
              color: '#9a3412',
              font: '12px/1.6 var(--es-font)',
            }}
          >
            {asked}
          </p>
        )}

        {errors.length > 0 && !isProblem && (
          <ul
            style={{
              margin: '10px 0 0',
              padding: '8px 10px 8px 26px',
              borderRadius: 'var(--es-radius-sm)',
              background: 'rgb(194 65 12 / 7%)',
              border: '1px solid rgb(194 65 12 / 18%)',
              color: '#9a3412',
              font: '12px/1.6 var(--es-font)',
            }}
          >
            {errors.map((error) => (
              <li key={`${String(error.line)}:${error.message}`}>
                <strong>Dòng {error.line}:</strong> {error.message}
                <code style={{ marginLeft: 6, opacity: 0.75 }}>{error.text}</code>
              </li>
            ))}
          </ul>
        )}

        {built !== null && errors.length === 0 && (
          <p style={{ margin: '10px 0 0', color: '#0f766e' }}>
            Đã vẽ {built} đối tượng. Nhấn <kbd>Ctrl</kbd>+<kbd>Z</kbd> để hoàn tác toàn bộ.
          </p>
        )}

        {/*
          The script, demoted from the way in to the thing that came out.

          It stays, and it stays editable, for the reason it was always in the box: a model can
          misread a problem, and the script is where that is visible and fixable. What changed is
          that it is no longer the *first* thing — a teacher who never opens this is a teacher the
          feature worked for, and one who does is looking at an answer rather than at a form.

          Opened automatically once there is a script to look at, closed while there is not.
        */}
        <details open={showScript} style={{ marginTop: 14 }}>
          <summary
            onClick={(event) => {
              event.preventDefault();
              setShowScript(!showScript);
            }}
            style={{ cursor: 'pointer', color: colour.body, font: '12px/1.6 var(--es-font)' }}
          >
            Script {built === null ? '(dành cho người dùng nâng cao)' : '— sửa được rồi vẽ lại'}
          </summary>

          <p style={{ margin: '6px 0', color: colour.muted, font: '11px/1.5 var(--es-font)' }}>
            Mỗi dòng một lệnh. Hình vẽ ra là các đối tượng bình thường — kéo được, đổi màu được,
            hoàn tác được.
          </p>

          <textarea
            value={script}
            spellCheck={false}
            aria-label="Script"
            onChange={(event) => {
              setScript(event.target.value);
            }}
            onKeyDown={(event) => {
              event.stopPropagation();
            }}
            className="es-field es-scroll"
            style={{
              minHeight: 150,
              padding: 10,
              font: '12px/1.6 var(--es-mono)',
              resize: 'vertical',
            }}
          />

          <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={build}
              disabled={script.trim() === ''}
              className="es-btn"
              style={{ padding: '5px 12px', font: '13px/1.2 var(--es-font)' }}
            >
              Vẽ lại từ script
            </button>
            <button
              type="button"
              onClick={() => {
                setScript(EXAMPLE_SCRIPT);
              }}
              className="es-btn"
              style={{ padding: '5px 12px', font: '13px/1.2 var(--es-font)' }}
            >
              Xem một script mẫu
            </button>
            <button
              type="button"
              onClick={() => {
                copy('grammar');
              }}
              className="es-btn"
              style={{ padding: '5px 12px', font: '13px/1.2 var(--es-font)' }}
            >
              {copied === 'grammar' ? 'Đã sao chép' : 'Sao chép cú pháp'}
            </button>
          </div>

          <details style={{ marginTop: 10 }}>
            <summary style={{ cursor: 'pointer', color: colour.muted, fontSize: 12 }}>
              Danh sách lệnh ({STATEMENTS.length})
            </summary>
            <table
              style={{
                width: '100%',
                marginTop: 8,
                borderCollapse: 'collapse',
                font: '12px/1.5 var(--es-font)',
              }}
            >
              <tbody>
                {STATEMENTS.map((statement) => (
                  <tr key={statement.keyword} style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                    <td style={{ padding: '4px 8px 4px 0', whiteSpace: 'nowrap' }}>
                      <code>
                        {statement.keyword}{' '}
                        {statement.parameters.map((parameter) => parameter.name).join(' ')}
                      </code>
                    </td>
                    <td style={{ padding: '4px 0', color: colour.muted }}>{statement.summary}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        </details>
      </div>

      {/*
        One primary action, and it is the one the panel exists for.

        The footer used to lead with `Vẽ`, which built the *script* — the advanced path — while
        the thing almost everyone wants sat third, in an outline button, next to two copy
        buttons. Drawing from the problem is now the only filled button on the panel.
      */}
      <footer
        style={{
          display: 'flex',
          gap: 8,
          padding: '10px 14px',
          borderTop: '1px solid rgba(0,0,0,0.08)',
        }}
      >
        {onDrawFromProblem !== undefined &&
          (asking ? (
            /*
             * The same slot, holding the way out.
             *
             * A disabled "Đang vẽ…" is what used to sit here, and for a wait measured in minutes
             * that is the wrong control: the reader has no way to change their mind about a
             * request that is still running, and no way to tell a slow one from a stuck one.
             */
            <button
              type="button"
              onClick={cancel}
              className="es-btn"
              style={{ padding: '7px 16px', font: '13px/1.2 var(--es-font)' }}
            >
              Huỷ
            </button>
          ) : (
            <button
              type="button"
              disabled={source.trim() === ''}
              onClick={draw}
              style={{
                padding: '7px 18px',
                borderRadius: 6,
                border: 'none',
                background: source.trim() === '' ? 'var(--es-hairline-strong)' : colour.accent,
                color: colour.solid,
                font: '600 13px/1.2 var(--es-font)',
                cursor: source.trim() === '' ? 'default' : 'pointer',
              }}
            >
              Vẽ hình
            </button>
          ))}

        {/*
          The way through without a model server: the grammar, worked examples out of the corpus,
          and the problem. Kept because a deployment without a key is a supported state.
        */}
        <button
          type="button"
          disabled={source.trim() === ''}
          onClick={() => {
            copy('prompt');
          }}
          className="es-btn"
          style={{
            padding: '7px 14px',
            font: '13px/1.2 var(--es-font)',
            cursor: source.trim() === '' ? 'default' : 'pointer',
          }}
        >
          {copied === 'prompt' ? 'Đã sao chép' : 'Sao chép prompt'}
        </button>
      </footer>
    </div>
  );
}
